import { faInfoCircle } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Tooltip, useMantineTheme } from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import { useState, useEffect, useRef } from 'react'
import { AdStatuses } from '../../utils/constants'

interface AdStatusTooltipProps {
  status: string
  children: React.ReactNode
  isHostMode?: boolean
}

const getStatusTooltipContent = (
  status: string,
  isHostMode: boolean = false,
): string => {
  if (isHostMode) {
    switch (status) {
      case AdStatuses.AwaitingCheckout:
        return "An advertiser has submitted an ad for your location but hasn't completed payment yet."
      case AdStatuses.PendingContentCheck:
        return 'This advertisement is being reviewed by our content team for compliance and quality standards.'
      case AdStatuses.PendingApproval:
        return 'This advertisement is waiting for your approval to run at your location.'
      case AdStatuses.PendingAdvertiserApproval:
        return "You've approved this advertisement. The advertiser needs to confirm before it goes live."
      case AdStatuses.PendingDesign:
        return 'The advertiser has requested a custom design. Our team is creating the advertisement.'
      case AdStatuses.ChangesRequested:
        return "You've requested changes to this advertisement. The advertiser needs to update it."
      case AdStatuses.Upcoming:
        return 'This advertisement is scheduled to start running at your location on the specified date.'
      case AdStatuses.Running:
        return 'This advertisement is currently being displayed at your location and earning you revenue.'
      case AdStatuses.Completed:
        return 'This advertisement has finished running at your location.'
      case AdStatuses.Denied:
        return "You've denied this advertisement from running at your location."
      case AdStatuses.PaymentDeclined:
        return "The advertiser's payment was declined. The advertisement cannot run until payment is resolved."
      case AdStatuses.Disabled:
        return 'This advertisement has been temporarily disabled at your location.'
      case 'Expired':
        return 'This advertisement has expired and is no longer running at your location.'
      case 'Archived':
        return 'This advertisement has been archived and is no longer active at your location.'
      default:
        return 'Advertisement status information for your location'
    }
  } else {
    switch (status) {
      case AdStatuses.AwaitingCheckout:
        return 'Your advertisement is ready for checkout. Complete payment to proceed with approval.'
      case AdStatuses.PendingContentCheck:
        return 'Your advertisement is being reviewed by our content team for compliance and quality standards.'
      case AdStatuses.PendingApproval:
        return 'Your advertisement is waiting for host approval at the selected location.'
      case AdStatuses.PendingAdvertiserApproval:
        return 'The host has approved your advertisement. Please review and confirm to proceed.'
      case AdStatuses.PendingDesign:
        return 'Your custom design request is being created by our design team.'
      case AdStatuses.ChangesRequested:
        return 'Changes have been requested for your advertisement. Please review and update as needed.'
      case AdStatuses.Upcoming:
        return 'Your advertisement is scheduled to start running at the specified date and time.'
      case AdStatuses.Running:
        return 'Your advertisement is currently active and being displayed at the selected location.'
      case AdStatuses.Completed:
        return 'Your advertisement has finished running and is no longer active.'
      case AdStatuses.Denied:
        return 'Your advertisement was not approved. Please review the feedback and make necessary changes.'
      case AdStatuses.PaymentDeclined:
        return 'Payment for your advertisement was declined. Please update your payment method.'
      case AdStatuses.Disabled:
        return 'Your advertisement has been temporarily disabled. Contact support for assistance.'
      case 'Expired':
        return 'Your advertisement has expired and is no longer running.'
      case 'Archived':
        return 'Your advertisement has been archived and is no longer active.'
      default:
        return 'Advertisement status information'
    }
  }
}

export const AdStatusTooltip = ({
  status,
  children,
  isHostMode = false,
}: AdStatusTooltipProps) => {
  const tooltipContent = getStatusTooltipContent(status, isHostMode)
  const isMobile = useMediaQuery('(max-width: 768px)')
  const [opened, setOpened] = useState(false)
  const theme = useMantineTheme()
  const containerRef = useRef<HTMLDivElement>(null)

  const handleClick = () => {
    if (isMobile) {
      setOpened(!opened)
    }
  }

  // Close tooltip when clicking outside on mobile
  useEffect(() => {
    if (!isMobile || !opened) return

    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpened(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isMobile, opened])

  return (
    <Tooltip
      label={tooltipContent}
      position='top'
      withArrow
      openDelay={isMobile ? 0 : 500}
      closeDelay={isMobile ? 0 : 200}
      multiline
      w={300}
      opened={isMobile ? opened : undefined}
      withinPortal={isMobile}
      zIndex={1000}
      styles={{
        tooltip: {
          backgroundColor: theme.colors.dark[7],
          color: theme.colors.gray[0],
          border: `1px solid ${theme.colors.gray[6]}`,
          fontSize: '14px',
          lineHeight: '1.4',
          maxWidth: '280px',
          wordWrap: 'break-word',
        },
        arrow: {
          borderColor: theme.colors.gray[6],
        },
      }}
    >
      <div
        ref={containerRef}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          cursor: isMobile ? 'pointer' : 'help',
        }}
        onClick={handleClick}
      >
        {children}
        <FontAwesomeIcon
          icon={faInfoCircle}
          size='xs'
          style={{
            color: '#888',
            opacity: 0.7,
            pointerEvents: 'none', // Prevent icon from interfering with click
          }}
        />
      </div>
    </Tooltip>
  )
}
